/**
SELLERIE MARINE - SERVICE API DEVIS

📋 DESCRIPTION:
Service TypeScript pour la gestion complète des devis avec API backend et fallback localStorage.
Interface unifiée pour toutes les opérations CRUD devis avec authentification JWT.

🎯 RESPONSABILITÉS:
- CRUD complet devis avec API backend sécurisée
- Fallback intelligent localStorage en cas d'erreur backend
- Gestion données démonstration pour développement
- Synchronisation bidirectionnelle backend/localStorage
- Gestion erreurs robuste avec récupération automatique

🔗 DÉPENDANCES:
- ApiService: Configuration base URL et utilitaires API
- Types: Interface Devis et structures de données
- LocalStorage: Stockage persistant fallback et backup
- JWT: Authentification via tokens Bearer

📡 INTERACTIONS:
- Backend: API REST /devis avec authentification complète
- Frontend: Configurateur, pages devis, dashboard
- Storage: Backup automatique localStorage pour résilience
- Auth: Récupération automatique tokens et user ID
- Fallback: Basculement transparent en cas d'erreur réseau

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Fallback localStorage automatique pour résilience
- Données démo intégrées pour développement
- Synchronisation backup localStorage après chaque opération
- Gestion erreurs avec récupération gracieuse
- Support création/modification avec détection automatique
- Logging détaillé pour debugging et monitoring
*/
// Service pour gérer les devis avec le localStorage

import { apiService } from './ApiService';
import { Devis } from './types';
import { FallbackAlerts } from './FallbackNotificationService';
import { apiFetch } from './httpClient';

// Ajout d'une exportation vide pour que le fichier soit considéré comme un module
export {};

/**
 * Classe pour gérer les devis
 */
class DevisApiService {

  /**
   * Récupère la liste des devis
   */
  async getDevis(archived: boolean = false): Promise<Devis[]> {
    try {
      const query = archived ? '?archived=true' : '';
      const devisList = await apiFetch<Devis[]>(`/devis${query}`);
      return devisList;
    } catch (error) {
      console.error('Erreur lors de la récupération des devis:', error);
      
      // 🚨 NOTIFICATION FALLBACK - Devis serveur indisponibles
      FallbackAlerts.devis('Récupération devis depuis API backend');
      
      // Fallback vers localStorage en cas d'erreur backend
      const storedDevis = localStorage.getItem('devis');
      if (storedDevis) {
        const parsed = JSON.parse(storedDevis);
        if (archived) {
          return parsed.filter((d: any) => d.isArchived || d.archivedAt);
        }
        return parsed.filter((d: any) => !d.isArchived && !d.archivedAt);
      }
      // Retourner des données de démonstration en cas d'erreur
      const demo = this.getDevisDemo();
      return archived ? demo.filter((d: any) => d.isArchived || d.archivedAt) : demo;
    }
  }

  /**
   * Récupère un devis par son ID avec retry automatique
   */
  async getDevisById(id: string): Promise<Devis> {
    const maxTentatives = 3;
    let tentatives = 0;
    
    while (tentatives < maxTentatives) {
      tentatives++;

      try {
        const devis = await apiFetch<Devis>(`/devis/${id}`, {
          cache: 'no-cache'
        });
        return devis;
        
      } catch (error: any) {
        console.error(`❌ [DevisApiService] Tentative ${tentatives}/${maxTentatives} échouée pour devis ${id}:`, {
          error: error.message,
          type: error.name,
          isNetworkError: error.message.includes('ERR_NETWORK') || error.message.includes('Failed to fetch'),
          stack: error.stack?.split('\n').slice(0, 3).join('\n')
        });
        
        // Si c'est une erreur réseau et qu'il reste des tentatives, réessayer
        if (tentatives < maxTentatives && (
          error.message.includes('ERR_NETWORK') || 
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError')
        )) {
          const delaiAttente = 1000 * tentatives; // Délai progressif
          await new Promise(resolve => setTimeout(resolve, delaiAttente));
          continue;
        }
        
        // Si toutes les tentatives ont échoué ou si c'est une erreur non-réseau
        console.error(`❌ [DevisApiService] Toutes les tentatives ont échoué pour devis ${id}`);
        
        // Propager l'erreur au lieu d'utiliser un fallback silencieux
        throw new Error(`Impossible de récupérer le devis ${id} après ${maxTentatives} tentatives. ${error.message}`);
      }
    }
    
    // Cette ligne ne devrait jamais être atteinte, mais TypeScript l'exige
    console.error(`❌ [DevisApiService] Code inaccessible atteint pour devis ${id}`);
    return this.getDevisDetailDemo(id);
  }

  /**
   * Sauvegarde un devis
   */
  async saveDevis(devis: Devis): Promise<Devis> {
    try {
      const method = devis.id ? 'PUT' : 'POST';
      const path = devis.id ? `/devis/${devis.id}` : '/devis';
      
      const savedDevis = await apiFetch<Devis>(path, {
        method,
        body: JSON.stringify(devis)
      });
      
      // Sauvegarder aussi en localStorage comme backup
      try {
        const storedDevis = localStorage.getItem('devis');
        let devisList: Devis[] = storedDevis ? JSON.parse(storedDevis) : [];
        
        if (savedDevis.id) {
          const index = devisList.findIndex(d => d.id === savedDevis.id);
          if (index >= 0) {
            devisList[index] = savedDevis;
          } else {
            devisList.push(savedDevis);
          }
        } else {
          devisList.push(savedDevis);
        }
        
        localStorage.setItem('devis', JSON.stringify(devisList));
      } catch (localError) {
        console.warn("⚠️ Erreur backup localStorage:", localError);
      }
      
      return savedDevis;
    } catch (error) {
      console.error('❌ Erreur sauvegarde backend:', error);
      
      // 🚨 NOTIFICATION FALLBACK CRITIQUE - Sauvegarde devis
      FallbackAlerts.sauvegarde('Sauvegarde devis vers API backend');
      
      // Fallback vers localStorage en cas d'erreur backend
      try {
        const storedDevis = localStorage.getItem('devis');
        let devisList: Devis[] = storedDevis ? JSON.parse(storedDevis) : [];
        
        let savedDevis: Devis;
        
        if (devis.id) {
          const index = devisList.findIndex(d => d.id === devis.id);
          if (index >= 0) {
            devisList[index] = devis;
          } else {
            devisList.push(devis);
          }
          savedDevis = devis;
        } else {
          const newDevis = {
            ...devis,
            id: `devis-${Date.now()}`,
            reference: devis.reference || `DEV-${new Date().getFullYear()}-${String(devisList.length + 1).padStart(3, '0')}`
          };
          devisList.push(newDevis);
          savedDevis = newDevis;
        }
        
        localStorage.setItem('devis', JSON.stringify(devisList));

        return savedDevis;
      } catch (localError) {
        console.error('❌ Erreur fallback localStorage:', localError);
        throw new Error('Impossible de sauvegarder le devis');
      }
    }
  }

  /**
   * Invalide l'optimisation d'un devis (marque le calepinage comme obsolète)
   */
  async invaliderOptimisation(devisId: string): Promise<void> {
    try {
      await apiFetch(`/devis/${devisId}/invalider-optimisation`, {
        method: 'POST'
      });
      console.log(`✅ [DevisApiService] Optimisation invalidée pour devis ${devisId}`);
    } catch (error) {
      console.error('⚠️ [DevisApiService] Erreur invalidation optimisation (non bloquant):', error);
      // Ne pas propager l'erreur - l'invalidation est une opération best-effort
    }
  }

  /**
   * Supprime un devis
   */
  async deleteDevis(id: string): Promise<void> {
    try {
      await apiFetch(`/devis/${id}`, { method: 'DELETE' });

      // Supprimer aussi du localStorage comme backup
      try {
        const storedDevis = localStorage.getItem('devis');
        if (storedDevis) {
          let devisList: Devis[] = JSON.parse(storedDevis);
          devisList = devisList.filter(d => d.id !== id);
          localStorage.setItem('devis', JSON.stringify(devisList));
        }
      } catch (localError) {
        console.warn("⚠️ Erreur backup localStorage:", localError);
      }
      
    } catch (error) {
      console.error('❌ Erreur suppression backend:', error);
      
      // 🚨 NOTIFICATION FALLBACK - Suppression devis
      FallbackAlerts.sauvegarde('Suppression devis via API backend');
      
      // Fallback vers localStorage en cas d'erreur backend
      try {
        const storedDevis = localStorage.getItem('devis');
        if (storedDevis) {
          let devisList: Devis[] = JSON.parse(storedDevis);
          devisList = devisList.filter(d => d.id !== id);
          localStorage.setItem('devis', JSON.stringify(devisList));
        }
      } catch (localError) {
        console.error('❌ Erreur fallback localStorage:', localError);
        throw new Error('Impossible de supprimer le devis');
      }
    }
  }

  async archiveDevis(id: string): Promise<Devis> {
    const devis = await apiFetch<Devis>(`/devis/${id}/archive`, {
      method: 'POST'
    });

    try {
      const stored = localStorage.getItem('devis');
      if (stored) {
        const list: Devis[] = JSON.parse(stored);
        const updated = list.map(d => d.id === id ? devis : d);
        localStorage.setItem('devis', JSON.stringify(updated));
      }
    } catch (e) {
      console.warn('⚠️ Erreur backup localStorage (archive):', e);
    }
    return devis;
  }

  async unarchiveDevis(id: string): Promise<Devis> {
    const devis = await apiFetch<Devis>(`/devis/${id}/unarchive`, {
      method: 'POST'
    });

    try {
      const stored = localStorage.getItem('devis');
      if (stored) {
        const list: Devis[] = JSON.parse(stored);
        const exists = list.some(d => d.id === id);
        const updated = exists ? list.map(d => d.id === id ? devis : d) : [...list, devis];
        localStorage.setItem('devis', JSON.stringify(updated));
      }
    } catch (e) {
      console.warn('⚠️ Erreur backup localStorage (unarchive):', e);
    }
    return devis;
  }

  /**
   * Retourne des devis de démonstration
   */
  private getDevisDemo(): Devis[] {
    return [
      {
        id: '1',
        reference: 'DEV-2025-001',
        client: '1',
        date: '2025-05-03',
        articles: [
          {
            id: '1',
            nom: 'Coussin banquette principale',
            type: 'coussin',
            forme: 'rectangulaire_bandeau',
            dimensions: {
              longueur: 120,
              largeur: 60,
              hauteur: 10,
              talon: 1,
            },
            options: {
              fond: true,
              grille: false,
              mousse: 'souple',
              fermeture: 'glissiere',
              surpiqure: true,
              formeComplexe: false,
            },
            materiaux: {
              dessus: { matiere: '1', couleur: 'Blanc' },
              dessous: { matiere: '1', couleur: 'Blanc' },
              cotes: { matiere: '1', couleur: 'Bleu marine' },
            },
            quantite: 2,
            prixUnitaire: 1400,
            nomTechnique: 'Rectangulaire avec bandeau 120x60x10 (avec fond, fermeture glissiere, surpiqûre)',
          }
        ],
        groupesMatiere: [],
        coutMatiereTotal: 1200,
        coutMainOeuvreTotal: 1500,
        coutAccessoiresTotal: 100,
        coutPetitesFournitures: 50,
        montantTotal: 2850
      },
      {
        id: '2',
        reference: 'DEV-2025-002',
        client: '2',
        date: '2025-05-05',
        articles: [
          {
            id: '2',
            nom: 'Coussin banquette arrière',
            type: 'coussin',
            forme: 'rectangulaire_bandeau',
            dimensions: {
              longueur: 100,
              largeur: 50,
              hauteur: 8,
              talon: 1,
            },
            options: {
              fond: true,
              grille: false,
              mousse: 'souple',
              fermeture: 'glissiere',
              surpiqure: false,
              formeComplexe: false,
            },
            materiaux: {
              dessus: { matiere: '2', couleur: 'Blanc' },
              dessous: { matiere: '2', couleur: 'Blanc' },
              cotes: { matiere: '2', couleur: 'Gris clair' },
            },
            quantite: 1,
            prixUnitaire: 1100,
            nomTechnique: 'Rectangulaire avec bandeau 100x50x8 (avec fond, fermeture glissiere)',
          }
        ],
        groupesMatiere: [],
        coutMatiereTotal: 800,
        coutMainOeuvreTotal: 1000,
        coutAccessoiresTotal: 80,
        coutPetitesFournitures: 30,
        montantTotal: 1910
      }
    ];
  }

  /**
   * Retourne un devis de démonstration détaillé
   */
  private getDevisDetailDemo(id: string): Devis {
    const devis = this.getDevisDemo();
    const devisFound = devis.find(d => d.id === id);
    
    if (devisFound) {
      return devisFound;
    }
    
    return {
      id,
      reference: `DEV-2025-${id}`,
      client: '1',
      date: '2025-05-09',
      articles: [
        {
          id: '1',
          nom: 'Coussin standard',
          type: 'coussin',
          forme: 'rectangulaire_bandeau',
          dimensions: {
            longueur: 100,
            largeur: 50,
            hauteur: 10,
            talon: 1,
          },
          options: {
            fond: true,
            grille: false,
            mousse: 'souple',
            fermeture: 'glissiere',
            surpiqure: false,
            formeComplexe: false,
          },
          materiaux: {
            dessus: { matiere: '1', couleur: 'Blanc' },
            dessous: { matiere: '1', couleur: 'Blanc' },
            cotes: { matiere: '1', couleur: 'Blanc' },
          },
          quantite: 1,
          prixUnitaire: 1000,
          nomTechnique: 'Rectangulaire avec bandeau 100x50x10 (avec fond, fermeture glissiere)',
        }
      ],
      groupesMatiere: [],
      coutMatiereTotal: 600,
      coutMainOeuvreTotal: 800,
      coutAccessoiresTotal: 50,
      coutPetitesFournitures: 20,
      montantTotal: 1470
    };
  }
}

// Exporter une instance unique du service
export const devisApiService = new DevisApiService();
